import { create } from 'zustand';

import { Anchor, Comment, isAnchorEqual } from '../../types';

export type CommentCreatedHandler = ({
  anchor,
  threadId,
  reason,
  data,
}: {
  anchor?: Anchor;
  threadId: string;
  reason: 'created';
  data: any;
}) => void;

interface CommentsState {
  // true if we are in comment mode
  commentMode: boolean;

  // true if a CommentsProvider is mounted (comments are available on this page)
  commentsEnabled: boolean;

  // enable readOnly to prevent adding new comments
  readOnly: boolean;

  // current set of known Comments and the comment that is currently
  // selected, if any
  comments: Comment[];

  selectedComment?: string;

  updateComments: (comments: Comment[]) => void;
  selectComment: (id: string, anchor?: Anchor) => void;
  deselectComment: () => void;

  selectAnchor: (anchor: Anchor) => void;
  deselectAnchor: () => void;

  hoverAnchor: (anchor: Anchor) => void;
  unhoverAnchor: (anchor: Anchor) => void;

  // id of the zone that is currently selected for commenting
  selectedAnchor?: Anchor;

  // id of the zone that we should highlight
  hoveredAnchor?: Anchor;

  // id of the comment that is currently being created
  creatingAnchor?: Anchor;

  comment: (anchor: Anchor) => void;
  created: () => void;

  // for entering comment mode
  enterCommentMode: () => void;

  // for enabling/disabling comments
  enableComments: () => void;
  disableComments: () => void;

  // for toggling readOnly mode
  toggleReadOnly: (readOnly: boolean) => void;

  // add listener
  listeners: CommentCreatedHandler[];

  addListener: (listener: CommentCreatedHandler) => () => void;
}

export const useCommentState = create<CommentsState>(set => ({
  comments: [],
  selectedComment: undefined,
  selectedAnchor: undefined,
  hoveredAnchor: undefined,
  creatingAnchor: undefined,
  commentMode: false,
  commentsEnabled: false,
  readOnly: false,
  listeners: [],

  // Update the state with known comments.
  updateComments: (comments: Comment[]) =>
    set(state => ({
      ...state,
      comments,
    })),

  comment: (anchor: Anchor) =>
    set(state => {
      return {
        ...state,
        creatingAnchor: anchor,
        commentMode: true,
        selectedComment: undefined,
        selectedAnchor: anchor,
        hoveredAnchor: undefined,
      };
    }),

  created: () =>
    set(state => ({
      ...state,
      creatingAnchor: undefined,
      selectedAnchor: undefined,
    })),

  selectAnchor: (anchor: Anchor) =>
    set(state => ({
      ...state,
      hoveredAnchor: undefined,
      selectedAnchor: anchor,
      commentMode: true,
    })),

  hoverAnchor: (anchor: Anchor) =>
    set(state => ({
      ...state,
      hoveredAnchor: anchor,
    })),

  unhoverAnchor: (anchor: Anchor) =>
    set(state => ({
      ...state,
      hoveredAnchor:
        state.hoveredAnchor && isAnchorEqual(state.hoveredAnchor, anchor)
          ? undefined
          : state.hoveredAnchor,
    })),

  selectComment: (commentId: string, anchor?: Anchor) =>
    set(state => ({
      ...state,
      selectedComment: commentId,
      selectedAnchor: anchor,
      commentMode: true,
      creatingAnchor: undefined,
    })),

  deselectComment: () =>
    set(state => ({
      ...state,
      selectedComment: undefined,
      selectedAnchor: undefined,
    })),

  enterCommentMode: () =>
    set(state => ({
      ...state,
      commentMode: true,
    })),

  enableComments: () =>
    set(state => ({
      ...state,
      commentsEnabled: true,
    })),

  disableComments: () =>
    set(state => ({
      ...state,
      commentsEnabled: false,
      commentMode: false,
      selectedAnchor: undefined,
      hoveredAnchor: undefined,
      creatingAnchor: undefined,
      selectedComment: undefined,
    })),

  toggleReadOnly: (readOnly: boolean) => set(state => ({ ...state, readOnly })),

  addListener: (listener: CommentCreatedHandler) => {
    set(state => ({
      ...state,
      listeners: [...state.listeners, listener],
    }));

    return () => {
      set(state => ({
        ...state,
        listeners: state.listeners.filter(l => l !== listener),
      }));
    };
  },

  deselectAnchor: () =>
    set(state => ({
      ...state,
      hoveredAnchor: undefined,
      selectedAnchor: undefined,
      commentMode: false,
      creatingAnchor: undefined,
    })),
}));
